// Fase 16 — MFA TOTP: verifica secondo fattore ed enrollment con QR code.
// Il segreto resta solo nel URI otpauth:// renderizzato come QR (mai loggato).
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Loader2, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function MfaFlow({ status, mfaToken, onSuccess, onBack }) {
  const [mode, setMode] = useState(
    status === "MFA_ENROLLMENT_REQUIRED" ? "enroll" : "verify");
  const [uri, setUri] = useState(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode !== "enroll") return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/auth/mfa/enroll/start",
          { mfa_token: mfaToken });
        setUri(data.otpauth_uri);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, mfaToken]);

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/enroll/confirm",
        { mfa_token: mfaToken, code });
      setRecovery(data.recovery_codes);
      setCode("");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify",
        { mfa_token: mfaToken, code });
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const copyRecovery = () => {
    navigator.clipboard.writeText(recovery.join("\n"));
    toast.success("Codici copiati negli appunti");
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="mfa-flow">
      <CardContent className="p-8">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-green-700" aria-hidden="true" />
          <h2 className="text-xl font-bold text-slate-900">
            Verifica in due passaggi
          </h2>
        </div>

        {error && (
          <div role="alert" data-testid="mfa-error"
            className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
            {error}
          </div>
        )}

        {recovery ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Configurazione completata. Salva questi <strong>codici di recupero</strong>:
              sono mostrati <strong>una sola volta</strong> e ognuno può essere usato
              una volta sola se perdi l'accesso all'app di autenticazione.
            </p>
            <pre className="rounded-md bg-slate-100 border border-slate-200 p-3 text-xs leading-6 select-all"
              data-testid="mfa-recovery-codes">{recovery.join("\n")}</pre>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={copyRecovery}
                data-testid="mfa-copy-recovery-button">
                <Copy className="w-4 h-4 mr-1" aria-hidden="true" /> Copia
              </Button>
              <Button type="button" className="bg-green-700 hover:bg-green-800"
                data-testid="mfa-continue-button"
                onClick={() => { setRecovery(null); setMode("verify"); }}>
                Ho salvato i codici — Continua
              </Button>
            </div>
          </div>
        ) : mode === "enroll" ? (
          <form onSubmit={confirmEnroll} className="space-y-4">
            <p className="text-sm text-slate-600">
              Il tuo ruolo richiede la verifica in due passaggi. Scansiona il QR con
              un'app di autenticazione (Google Authenticator, Authy, 1Password…)
              e inserisci il codice a 6 cifre per attivare la protezione.
            </p>
            <div className="flex justify-center py-2" data-testid="mfa-qr">
              {uri ? <QRCodeSVG value={uri} size={200} /> : (
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" aria-hidden="true" />)}
            </div>
            {uri && (
              <details className="text-xs text-slate-500">
                <summary>Non riesci a scansionare? Inserimento manuale</summary>
                <p className="mt-1 break-all select-all">{uri}</p>
              </details>
            )}
            <div className="space-y-2">
              <Label htmlFor="mfa-enroll-code">Codice a 6 cifre</Label>
              <Input id="mfa-enroll-code" inputMode="numeric" maxLength={6} required
                placeholder="123456" data-testid="mfa-enroll-code-input"
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            </div>
            <Button type="submit" disabled={loading || code.length !== 6}
              className="w-full bg-green-700 hover:bg-green-800"
              data-testid="mfa-confirm-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : "Attiva verifica in due passaggi"}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="space-y-4">
            <p className="text-sm text-slate-600">
              Inserisci il codice a 6 cifre dall'app di autenticazione, oppure un
              codice di recupero.
            </p>
            <div className="space-y-2">
              <Label htmlFor="mfa-code">Codice</Label>
              <Input id="mfa-code" autoComplete="one-time-code" required
                placeholder="123456" data-testid="mfa-code-input"
                value={code} onChange={(e) => setCode(e.target.value.trim())} />
            </div>
            <Button type="submit" disabled={loading || code.length < 6}
              className="w-full bg-green-700 hover:bg-green-800"
              data-testid="mfa-verify-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : "Verifica e accedi"}
            </Button>
          </form>
        )}

        <button type="button" onClick={onBack} data-testid="mfa-back-link"
          className="mt-4 text-xs text-slate-500 hover:text-slate-700 hover:underline">
          ← Torna al login
        </button>
      </CardContent>
    </Card>
  );
}
